import { useState } from 'react';

import type { PayRateRole, SetPayRateInput } from '../../../api/staff-pay-rates';

import { dollarsToCents, formatCalendarDate, MAX_RATE_CENTS } from './pay-rate-format';
import type { WriteResult } from './use-staff-pay-rates';

const FIELD_ERROR_KEYS = ['amountCents', 'effectiveFrom'];

function amountProblem(amount: string, cents: number | null): string | undefined {
  if (amount === '') return undefined;
  if (cents === null) return 'Enter an amount like 250 or 250.50';
  if (cents > MAX_RATE_CENTS) return "A rate can't be more than $1,000";
  return undefined;
}

function dateProblem(date: string, earliest: string): string | undefined {
  if (date === '') return 'Pick a date';
  if (date < earliest) return `Pick ${formatCalendarDate(earliest)} or later`;
  return undefined;
}

/** The amount and date being typed into the set-rate form, what is wrong with
 *  them, and the save. Errors the API sends back clear when either is edited. */
export function useSetRateDraft({
  role,
  today,
  earliest,
  busy,
  onSubmit,
  onSaved,
}: {
  role: PayRateRole;
  today: string;
  earliest: string;
  busy: boolean;
  onSubmit: (input: SetPayRateInput) => Promise<WriteResult>;
  onSaved: () => void;
}) {
  const [amount, setAmountText] = useState('');
  const [date, setDateText] = useState(today);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const cents = dollarsToCents(amount);
  const amountError = amountProblem(amount, cents);
  const dateError = dateProblem(date, earliest);

  async function save() {
    if (cents === null) return;
    const result = await onSubmit({ role, amountCents: cents, effectiveFrom: date });
    if (result.ok) onSaved();
    else setServerErrors(result.fieldErrors);
  }

  return {
    amount,
    date,
    setAmount: (value: string) => {
      setAmountText(value);
      setServerErrors({});
    },
    setDate: (value: string) => {
      setDateText(value);
      setServerErrors({});
    },
    amountError: amountError ?? serverErrors.amountCents,
    dateError: dateError ?? serverErrors.effectiveFrom,
    otherErrors: Object.entries(serverErrors)
      .filter(([field]) => !FIELD_ERROR_KEYS.includes(field))
      .map(([, message]) => message),
    canSave: !busy && cents !== null && !amountError && !dateError,
    save,
  };
}
